import { useState, useEffect, useRef } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'
import api from '../../services/api'
import toast from 'react-hot-toast'
import LoadingSpinner from '../../components/LoadingSpinner'
import {
  Sparkles,
  Send,
  Plus,
  Trash2,
  BookOpen,
  Layers,
  X,
  Award,
  History,
  Lightbulb,
  HelpCircle,
  RotateCcw,
  PenTool,
  Wrench,
  ChevronDown,
  ChevronUp,
  ClipboardCheck,
  Radio
} from 'lucide-react'
import AITutorLearningProfile from './AITutorLearningProfile'
import AITutorAssessment from './AITutorAssessment'

function inferFriendlyTeachingLabel(text) {
  const value = String(text || '').toLowerCase()
  if (/\b(not working|error|bug|stuck|troubleshoot|debug|broken)\b/.test(value)) return 'Troubleshooting'
  if (/\b(review|revise|recap|quiz me|test me|summarize)\b/.test(value)) return 'Review'
  if (/\b(practice|exercise|give me a task|let me try)\b/.test(value)) return 'Practice'
  if (/\b(project|promotional video|i want to (create|make|build)|shot list)\b/.test(value)) return 'Project coaching'
  if (/\b(explain|define|tell me about|walk me through|in detail|notes)\b/.test(value)) return 'Explaining'
  return 'Guiding discovery'
}

function renderInlineMarkdown(text, keyPrefix) {
  const parts = String(text || '').split(/(\*\*[^*]+\*\*|`[^`]+`)/g)
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return (
        <strong key={`${keyPrefix}-b-${index}`} className="text-white font-semibold">
          {part.slice(2, -2)}
        </strong>
      )
    }
    if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
      return (
        <code
          key={`${keyPrefix}-c-${index}`}
          className="px-1.5 py-0.5 bg-black/40 border border-white/10 font-mono text-xs sm:text-sm text-cinema-gold"
        >
          {part.slice(1, -1)}
        </code>
      )
    }
    return <span key={`${keyPrefix}-t-${index}`}>{part}</span>
  })
}

function TutorRichText({ content }) {
  const raw = String(content || '')
  const blocks = raw.split(/```([\s\S]*?)```/g)

  return (
    <div className="space-y-2.5 font-sans">
      {blocks.map((block, blockIndex) => {
        if (blockIndex % 2 === 1) {
          const lines = block.split('\n')
          const code = lines[0] && !lines[0].includes(' ') ? lines.slice(1).join('\n') : block
          return (
            <pre
              key={`code-${blockIndex}`}
              className="mt-2 mb-2 p-3.5 overflow-x-auto bg-black/50 border border-white/10 text-xs sm:text-sm font-mono text-cinema-soft leading-relaxed"
            >
              <code>{code.replace(/^\n/, '')}</code>
            </pre>
          )
        }

        const paragraphs = block.split(/\n{2,}/)
        return paragraphs.map((paragraph, pIndex) => {
          const lines = paragraph.split('\n').map((line) => line.trimEnd()).filter((line) => line.length > 0)
          if (lines.length === 0) return null

          const isBulletList = lines.every((line) => /^[-*•]\s+/.test(line.trim()))
          const isNumberedList = lines.every((line) => /^\d+\.\s+/.test(line.trim()))

          if (isBulletList) {
            return (
              <ul key={`ul-${blockIndex}-${pIndex}`} className="list-disc pl-4 space-y-1">
                {lines.map((line, i) => (
                  <li key={`li-${i}`}>{renderInlineMarkdown(line.trim().replace(/^[-*•]\s+/, ''), `ul-${blockIndex}-${pIndex}-${i}`)}</li>
                ))}
              </ul>
            )
          }

          if (isNumberedList) {
            return (
              <ol key={`ol-${blockIndex}-${pIndex}`} className="list-decimal pl-4 space-y-1">
                {lines.map((line, i) => (
                  <li key={`nli-${i}`}>{renderInlineMarkdown(line.trim().replace(/^\d+\.\s+/, ''), `ol-${blockIndex}-${pIndex}-${i}`)}</li>
                ))}
              </ol>
            )
          }

          return (
            <p key={`p-${blockIndex}-${pIndex}`} className="whitespace-pre-wrap">
              {lines.map((line, i) => (
                <span key={`ln-${i}`}>
                  {i > 0 && <br />}
                  {renderInlineMarkdown(line, `p-${blockIndex}-${pIndex}-${i}`)}
                </span>
              ))}
            </p>
          )
        })
      })}
    </div>
  )
}

function buildStarterPrompts(course, lesson) {
  const courseTitle = course?.title || 'this course'
  const lessonTitle = lesson?.title

  if (lessonTitle) {
    return [
      { label: 'Explain this lesson', text: `Please explain the key idea of "${lessonTitle}" in a practical way.`, icon: BookOpen },
      { label: 'Give me an example', text: `Give me a practical SMA example for "${lessonTitle}" and then ask me how I would apply it.`, icon: Lightbulb },
      { label: 'Quiz me', text: `Quiz me on "${lessonTitle}" to check my understanding. Ask one question at a time.`, icon: HelpCircle },
      { label: 'Practice this topic', text: `Give me a short practice task for "${lessonTitle}" and wait for my attempt before giving feedback.`, icon: PenTool },
      { label: 'Review this lesson', text: `Help me review "${lessonTitle}". Summarize the important concepts briefly, then ask me a short question.`, icon: RotateCcw },
      { label: "What's my progress?", text: `What's my progress in this course? What should I learn next?`, icon: Award },
      { label: 'Explain my assignment', text: `What assignment is connected to "${lessonTitle}"? Help me understand what I need to do without doing it for me.`, icon: ClipboardCheck },
    ]
  }

  return [
    { label: 'Explain a concept', text: `Help me understand the most important concept in ${courseTitle}. Ask what I already know first.`, icon: BookOpen },
    { label: 'Practical example', text: `Give me a practical professional example from ${courseTitle}, then ask how I would apply it.`, icon: Lightbulb },
    { label: 'Quiz me', text: `Quiz me on ${courseTitle}. Ask one question at a time and wait for my answer.`, icon: HelpCircle },
    { label: 'Troubleshoot a project', text: `I need help troubleshooting a project in ${courseTitle}. Ask me what is going wrong before giving steps.`, icon: Wrench },
    { label: 'Review this course', text: `Help me review what I should know so far in ${courseTitle}. Ask a short question to find weak areas.`, icon: RotateCcw },
    { label: "What's my progress?", text: `What's my progress in ${courseTitle}? What should I learn next?`, icon: Award },
    { label: 'My assignments', text: `What assignments do I have in ${courseTitle}? Help me understand what I need to do.`, icon: ClipboardCheck },
  ]
}

export default function AITutorChat() {
  const { user } = useAuthStore()
  const [searchParams, setSearchParams] = useSearchParams()

  // State
  const [courses, setCourses] = useState([])
  const [selectedCourseId, setSelectedCourseId] = useState(null)
  const [selectedLessonId, setSelectedLessonId] = useState(null)
  const [sessions, setSessions] = useState([])
  const [currentSessionId, setCurrentSessionId] = useState(null)
  const [agentMode, setAgentMode] = useState(false) // Phase 6: tool-calling agent mode
  const [messages, setMessages] = useState([])
  const [inputText, setInputText] = useState('')
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [usage, setUsage] = useState({ turnsUsed: 0, dailyLimit: 100, remaining: 100 })
  const [teachingLabel, setTeachingLabel] = useState(null)
  const [learningProfile, setLearningProfile] = useState(null)
  const [showFullProgress, setShowFullProgress] = useState(false)
  const [assessment, setAssessment] = useState(null)
  const [assessmentAnswer, setAssessmentAnswer] = useState('')
  const [assessmentResult, setAssessmentResult] = useState(null)
  const [assessmentSubmitting, setAssessmentSubmitting] = useState(false)
  const [contextOpen, setContextOpen] = useState(true)
  const [chatHeight, setChatHeight] = useState(null)
  const [keyboardInset, setKeyboardInset] = useState(0)
  const [liveJoin, setLiveJoin] = useState(null)

  const messagesEndRef = useRef(null)
  const textareaRef = useRef(null)

  const queryCourseId = searchParams.get('courseId')
  const queryLessonId = searchParams.get('lessonId')
  const querySessionId = searchParams.get('sessionId')
  const studentFirstName = user?.first_name ? String(user.first_name).trim() : ''

  // Auto-scroll messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages, sending])

  useEffect(() => {
    if (messages.length === 1) setContextOpen(false)
  }, [messages.length])

  useEffect(() => {
    const viewport = window.visualViewport
    const sync = () => {
      const isMobile = window.matchMedia('(max-width: 639px)').matches
      if (!isMobile) {
        setChatHeight(null)
        setKeyboardInset(0)
        return
      }
      const navHeight = 64
      const available = (viewport?.height || window.innerHeight) - navHeight
      setChatHeight(Math.max(280, Math.round(available)))
      if (viewport) {
        setKeyboardInset(Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop))
      }
    }

    sync()
    viewport?.addEventListener('resize', sync)
    viewport?.addEventListener('scroll', sync)
    window.addEventListener('resize', sync)
    return () => {
      viewport?.removeEventListener('resize', sync)
      viewport?.removeEventListener('scroll', sync)
      window.removeEventListener('resize', sync)
    }
  }, [])

  useEffect(() => {
    const apply = () => {
      const isMobile = window.matchMedia('(max-width: 639px)').matches
      document.body.style.overflow = isMobile ? 'hidden' : ''
    }
    apply()
    window.addEventListener('resize', apply)
    return () => {
      window.removeEventListener('resize', apply)
      document.body.style.overflow = ''
    }
  }, [])

  // Initial load: Courses, Usage, Sessions
  useEffect(() => {
    loadInitialData()
  }, [])

  useEffect(() => {
    setLiveJoin(null)
  }, [selectedCourseId])

  useEffect(() => {
    if (!selectedCourseId) return undefined

    let cancelled = false
    let timer = null
    let intervalMs = 15000

    const poll = async () => {
      try {
        const params = { courseId: selectedCourseId }
        if (selectedLessonId) params.lessonId = selectedLessonId
        const res = await api.get('/tutor/live-class', { params })
        if (cancelled) return
        if (res.data?.ok) {
          setLiveJoin(res.data)
          const nextInterval = Number(res.data.sync?.intervalMs)
          if (Number.isFinite(nextInterval) && nextInterval >= 5000) {
            intervalMs = nextInterval
          }
        }
      } catch (_) {
        // Keep last known snapshot. A missed poll is not "class ended".
      }
      if (!cancelled) {
        timer = window.setTimeout(poll, intervalMs)
      }
    }

    poll()
    return () => {
      cancelled = true
      if (timer) window.clearTimeout(timer)
    }
  }, [selectedCourseId, selectedLessonId])

  const loadLearningProfile = async (courseId) => {
    if (!courseId) {
      setLearningProfile(null)
      return
    }
    try {
      const res = await api.get('/tutor/learning-profile', { params: { courseId } })
      if (res.data?.ok) {
        setLearningProfile(res.data)
      }
    } catch (err) {
      console.error('Failed to load learning profile:', err)
    }
  }

  const loadInitialData = async () => {
    try {
      setLoading(true)
      const [coursesRes, usageRes, sessionsRes] = await Promise.all([
        api.get('/tutor/courses'),
        api.get('/tutor/usage'),
        api.get('/tutor/sessions?limit=30')
      ])

      const courseList = coursesRes.data.courses || []
      setCourses(courseList)
      setUsage(usageRes.data.usage || { turnsUsed: 0, dailyLimit: 100, remaining: 100 })
      const sessionList = sessionsRes.data.sessions || []
      setSessions(sessionList)

      // Determine initial course & session selection
      if (querySessionId) {
        loadSession(querySessionId)
      } else if (queryCourseId) {
        const found = courseList.find(c => String(c.id) === String(queryCourseId))
        if (found) {
          setSelectedCourseId(found.id)
          if (queryLessonId) {
            const foundLesson = found.lessons?.find(l => String(l.id) === String(queryLessonId))
            if (foundLesson && foundLesson.isUnlocked) {
              setSelectedLessonId(foundLesson.id)
            }
          }
        }
      } else if (courseList.length > 0) {
        setSelectedCourseId(courseList[0].id)
      }

      const initialCourseId = queryCourseId
        || (courseList.length > 0 ? courseList[0].id : null)
      if (initialCourseId) {
        loadLearningProfile(initialCourseId)
      }
    } catch (err) {
      console.error('Failed to load tutor data:', err)
      toast.error('Failed to initialize AI Tutor')
    } finally {
      setLoading(false)
    }
  }

  const loadSession = async (sessionId) => {
    try {
      setSending(true)
      const res = await api.get(`/tutor/sessions/${sessionId}`)
      const { session, messages: sessionMessages } = res.data
      setCurrentSessionId(session.id)
      setSelectedCourseId(session.course_id)
      setSelectedLessonId(session.lesson_id || null)
      setMessages(sessionMessages || [])
      loadLearningProfile(session.course_id)
      const lastUser = [...(sessionMessages || [])].reverse().find((msg) => msg.role === 'user')
      setTeachingLabel(lastUser ? inferFriendlyTeachingLabel(lastUser.content) : null)
      setSearchParams({ sessionId: session.id })
    } catch (err) {
      console.error('Failed to load session:', err)
      toast.error('Session not found or unavailable')
      setCurrentSessionId(null)
      setMessages([])
      setTeachingLabel(null)
      setSearchParams({})
    } finally {
      setSending(false)
      setSidebarOpen(false)
    }
  }

  const handleStartNewSession = () => {
    setCurrentSessionId(null)
    setMessages([])
    setTeachingLabel(null)
    setAssessment(null)
    setAssessmentAnswer('')
    setAssessmentResult(null)
    setSearchParams(selectedCourseId ? { courseId: selectedCourseId } : {})
    setSidebarOpen(false)
    setContextOpen(true)
  }

  const startQuickCheck = async ({ topic = '', source = 'tutor' } = {}) => {
    if (!selectedCourseId) {
      toast.error('Please select an enrolled course first.')
      return
    }
    if (usage.turnsUsed >= usage.dailyLimit) {
      toast.error('Daily question limit reached (100/day). Your quota will reset at midnight.')
      return
    }

    try {
      setAssessmentSubmitting(true)
      const res = await api.post('/tutor/assessments', {
        courseId: selectedCourseId,
        lessonId: selectedLessonId || undefined,
        topic: topic || selectedLesson?.title || undefined,
        source
      })
      if (res.data?.ok && res.data.assessment) {
        setAssessment(res.data.assessment)
        setAssessmentAnswer('')
        setAssessmentResult(null)
        if (res.data.usage) setUsage(res.data.usage)
        loadLearningProfile(selectedCourseId)
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Could not start a knowledge check. Please try again.'
      toast.error(errorMsg)
    } finally {
      setAssessmentSubmitting(false)
    }
  }

  const handleSubmitAssessmentAnswer = async () => {
    if (!assessment?.id || !assessment.currentQuestion || assessmentSubmitting) return
    const answer = String(assessmentAnswer || '').trim()
    if (!answer) return

    try {
      setAssessmentSubmitting(true)
      const res = await api.post(`/tutor/assessments/${assessment.id}/answers`, {
        questionId: assessment.currentQuestion.id,
        answer
      })
      if (res.data?.ok) {
        setAssessmentResult({
          result: res.data.result,
          feedback: res.data.feedback,
          explanation: res.data.explanation
        })
        setAssessment(res.data.assessment)
        if (res.data.assessment?.status === 'completed') {
          loadLearningProfile(selectedCourseId)
        }
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Could not submit that answer.'
      toast.error(errorMsg)
    } finally {
      setAssessmentSubmitting(false)
    }
  }

  const handleContinueAssessment = () => {
    setAssessmentResult(null)
    setAssessmentAnswer('')
  }

  const handleDeleteSession = async (e, sessionId) => {
    e.stopPropagation()
    if (!window.confirm('Are you sure you want to delete this conversation?')) return

    try {
      await api.delete(`/tutor/sessions/${sessionId}`)
      setSessions(prev => prev.filter(s => s.id !== sessionId))
      toast.success('Conversation deleted')
      if (currentSessionId === sessionId) {
        handleStartNewSession()
      }
    } catch (err) {
      console.error('Failed to delete session:', err)
      toast.error('Failed to delete session')
    }
  }

  const handleSendMessage = async (customText = null) => {
    const textToSend = customText || inputText
    const cleanText = String(textToSend || '').trim()

    if (!cleanText || sending) return
    if (!selectedCourseId) {
      toast.error('Please select an enrolled course first.')
      return
    }

    if (usage.turnsUsed >= usage.dailyLimit) {
      toast.error('Daily question limit reached (100/day). Your quota will reset at midnight.')
      return
    }

    if (/\b(test me|quiz me|test myself|knowledge check|quick check)\b/i.test(cleanText)
      && !/\b(assignment|graded|submission|homework)\b/i.test(cleanText)) {
      const topicMatch = cleanText.match(/\b(?:on|about)\s+(.+)$/i)
      startQuickCheck({
        topic: topicMatch ? topicMatch[1].replace(/[.?!]+$/, '').trim() : (selectedLesson?.title || ''),
        source: 'tutor'
      })
      setInputText('')
      return
    }

    // Optimistically append user message
    const tempUserMsg = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content: cleanText,
      created_at: new Date().toISOString()
    }

    setMessages(prev => [...prev, tempUserMsg])
    setInputText('')
    setSending(true)
    setTeachingLabel(inferFriendlyTeachingLabel(cleanText))

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
    }

    try {
      const payload = {
        sessionId: currentSessionId || undefined,
        courseId: selectedCourseId,
        lessonId: selectedLessonId || undefined,
        message: cleanText
      }

      // Phase 6: Use agent endpoint when agent mode is enabled
      const endpoint = agentMode ? '/tutor/agent' : '/tutor/chat'
      const res = await api.post(endpoint, payload)
      const { sessionId, message: assistantMsg, usage: updatedUsage, title } = res.data

      // If new session created, update state and session list
      if (!currentSessionId && sessionId) {
        setCurrentSessionId(sessionId)
        setSearchParams({ sessionId })
        setSessions(prev => [
          {
            id: sessionId,
            title: title || cleanText.slice(0, 30),
            course_id: selectedCourseId,
            lesson_id: selectedLessonId,
            course_code: selectedCourse?.courseCode || '',
            last_message_at: new Date().toISOString()
          },
          ...prev
        ])
      }

      // Update assistant message & usage
      setMessages(prev => [...prev, assistantMsg])
      if (assistantMsg?.teachingLabel) {
        setTeachingLabel(assistantMsg.teachingLabel)
      }
      if (updatedUsage) {
        setUsage(updatedUsage)
      }
      loadLearningProfile(selectedCourseId)
    } catch (err) {
      console.error('AI Tutor error:', err)
      const errorMsg = err.response?.data?.error || 'Failed to receive tutor response. Please try again.'
      toast.error(errorMsg)

      // Add system error bubble
      setMessages(prev => [
        ...prev,
        {
          id: `err-${Date.now()}`,
          role: 'assistant',
          content: `⚠️ ${errorMsg}`,
          isError: true,
          created_at: new Date().toISOString()
        }
      ])
    } finally {
      setSending(false)
    }
  }

  const handlePracticeRecommendation = (item) => {
    if (item?.action === 'check' || item?.action === 'challenge') {
      startQuickCheck({ topic: item.topic, source: 'recommendation' })
      return
    }
    const text = item?.message || (item?.topic ? `Help me practice ${item.topic}.` : null)
    if (text) handleSendMessage(text)
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  // Selected course and available lessons
  const selectedCourse = courses.find(c => c.id === selectedCourseId)
  const availableLessons = selectedCourse?.lessons || []
  const selectedLesson = availableLessons.find(l => l.id === selectedLessonId)
  const promptStarters = buildStarterPrompts(selectedCourse, selectedLesson)
  const quickActions = promptStarters.slice(0, 4)
  const keyboardOpen = keyboardInset > 80
  const liveLessonTitle = liveJoin?.applied && liveJoin.live?.lesson?.title
    ? liveJoin.live.lesson.title
    : null
  const contextSummary = liveLessonTitle
    ? `Live · ${liveLessonTitle}`
    : selectedLesson
    ? `${selectedCourse?.courseCode || 'Course'} · ${selectedLesson.title}`
    : (selectedCourse?.courseCode || selectedCourse?.title || 'Select a course')

  if (loading) {
    return <LoadingSpinner label="Loading AI Tutor session" />
  }

  return (
    <div
      className="flex flex-col relative min-h-0 h-[calc(100dvh-4rem)] sm:h-[calc(100vh-8rem)] sm:min-h-[550px]"
      style={chatHeight ? { height: chatHeight } : undefined}
    >
      {/* Header Bar */}
      <div className="flex items-center justify-between gap-2 px-3 py-2 sm:px-0 sm:py-0 sm:pb-4 border-b border-white/10 flex-shrink-0">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden p-2.5 min-h-[44px] min-w-[44px] cinema-glass border border-white/10 text-cinema-muted hover:text-white flex items-center justify-center"
            title="Open conversations"
            aria-label="Open conversations"
          >
            <History size={18} />
          </button>
          <div className="hidden sm:flex w-10 h-10 bg-cinema-glow/10 border border-cinema-glow/30 items-center justify-center text-cinema-glow shadow-lg shadow-cinema-glow/10 flex-shrink-0">
            <Sparkles size={20} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 sm:gap-2">
              <h1 className="font-display text-base sm:text-xl text-white font-medium truncate">SMA AI Tutor</h1>
              <span className="hidden sm:inline-flex px-2.5 py-0.5 text-xs font-mono bg-cinema-gold/10 text-cinema-gold border border-cinema-gold/25 font-medium">
                Academic Mentor
              </span>
              {teachingLabel && messages.length > 0 && !assessment && (
                <span className="hidden sm:inline-flex px-2.5 py-0.5 text-xs font-sans bg-cinema-glow/10 text-cinema-glow border border-cinema-glow/25 font-medium">
                  {teachingLabel}
                </span>
              )}
            </div>
            <p className="sm:hidden font-sans text-xs text-cinema-muted truncate">
              {teachingLabel && messages.length > 0 ? teachingLabel : contextSummary}
            </p>
            <p className="hidden sm:block font-sans text-xs sm:text-sm text-cinema-muted">
              Your personal SMA instructor for the course you are studying.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-1.5 sm:gap-3 flex-shrink-0">
          <div className="cinema-glass px-2.5 py-1.5 sm:px-3 border border-white/8 flex items-center gap-2">
            <div className="flex flex-col items-end">
              <span className="hidden sm:block font-sans text-xs text-cinema-muted uppercase tracking-wider font-medium">Daily Quota</span>
              <span className="font-mono text-xs sm:text-sm font-semibold text-cinema-soft">
                {usage.turnsUsed}/{usage.dailyLimit}
              </span>
            </div>
            <div className="hidden sm:block w-12 h-1.5 bg-white/10 overflow-hidden">
              <div
                className={`h-full transition-all duration-500 ${
                  usage.turnsUsed >= 90
                    ? 'bg-red-500'
                    : usage.turnsUsed >= 70
                    ? 'bg-yellow-500'
                    : 'bg-cinema-glow'
                }`}
                style={{ width: `${Math.min(100, (usage.turnsUsed / usage.dailyLimit) * 100)}%` }}
              />
            </div>
          </div>

          <button
            type="button"
            onClick={() => startQuickCheck({ source: selectedLessonId ? 'lesson' : 'tutor' })}
            disabled={sending || assessmentSubmitting || usage.turnsUsed >= usage.dailyLimit || !selectedCourseId}
            className="sm:hidden p-2.5 min-h-[44px] min-w-[44px] cinema-glass border border-cinema-gold/30 text-cinema-gold disabled:opacity-40 flex items-center justify-center"
            title="Take Quick Check"
            aria-label="Take Quick Check"
          >
            <ClipboardCheck size={16} />
          </button>
          <button
            type="button"
            onClick={handleStartNewSession}
            className="sm:hidden p-2.5 min-h-[44px] min-w-[44px] cinema-glass border border-white/10 text-cinema-muted hover:text-white flex items-center justify-center"
            title="New conversation"
            aria-label="New conversation"
          >
            <Plus size={16} />
          </button>

          <button
            type="button"
            onClick={() => startQuickCheck({ source: selectedLessonId ? 'lesson' : 'tutor' })}
            disabled={sending || assessmentSubmitting || usage.turnsUsed >= usage.dailyLimit || !selectedCourseId}
            className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-xs cinema-glass border border-cinema-gold/30 text-cinema-gold hover:text-white hover:border-cinema-gold/60 disabled:opacity-40"
            title="Take a short formative knowledge check"
          >
            <ClipboardCheck size={14} />
            <span>Take Quick Check</span>
          </button>

          <Link
            to="/student/ai-tutor"
            className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-xs cinema-btn-secondary border border-white/10 hover:border-cinema-gold/40 text-cinema-muted hover:text-white transition-all"
            title="View AI Assignment Audits"
          >
            <Award size={14} className="text-cinema-gold" />
            <span>AI Feedback</span>
          </Link>
        </div>
      </div>

      {/* Main Workspace: Sidebar + Chat Area */}
      <div className="flex flex-1 overflow-hidden pt-0 sm:pt-4 gap-0 sm:gap-4 min-h-0">
        {/* Mobile Sidebar Overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/70 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Session History Sidebar — bottom sheet on mobile */}
        <aside
          className={`
            fixed inset-x-0 bottom-0 z-50 max-h-[88dvh] cinema-glass-strong border-t border-white/10 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] flex flex-col rounded-t-xl
            lg:static lg:inset-auto lg:bottom-auto lg:max-h-none lg:w-72 lg:z-auto lg:h-full lg:flex-shrink-0 lg:rounded-none lg:border lg:border-white/10 lg:pb-4
            transition-transform duration-300 ease-in-out
            ${sidebarOpen ? 'translate-y-0' : 'translate-y-full pointer-events-none lg:pointer-events-auto lg:translate-y-0'}
          `}
        >
          <div className="lg:hidden w-10 h-1 bg-white/20 rounded-full mx-auto mb-3" />
          <div className="flex items-center justify-between pb-3 border-b border-white/10 mb-3">
            <span className="cinema-label text-xs">Conversations</span>
            <button
              type="button"
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-2 min-h-[44px] min-w-[44px] text-cinema-muted hover:text-white flex items-center justify-center"
              aria-label="Close conversations"
            >
              <X size={16} />
            </button>
          </div>

          <button
            onClick={handleStartNewSession}
            className="w-full flex items-center justify-center gap-2 py-2.5 px-4 cinema-btn-primary !min-h-[44px] !px-4 text-xs font-medium mb-3 shadow-lg shadow-cinema-glow/10"
          >
            <Plus size={16} />
            <span>New Conversation</span>
          </button>

          <div className="mb-3">
            <AITutorLearningProfile
              compact
              profile={learningProfile}
              disabled={sending || usage.turnsUsed >= usage.dailyLimit}
              onPractice={handlePracticeRecommendation}
            />
            <button
              type="button"
              onClick={() => setShowFullProgress((open) => !open)}
              className="mt-1.5 w-full flex items-center justify-center gap-1 text-xs text-cinema-muted hover:text-white font-medium"
            >
              {showFullProgress ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              {showFullProgress ? 'Hide progress' : 'View progress'}
            </button>
            {showFullProgress && (
              <div className="mt-2">
                <AITutorLearningProfile
                  profile={learningProfile}
                  disabled={sending || usage.turnsUsed >= usage.dailyLimit}
                  onPractice={handlePracticeRecommendation}
                />
              </div>
            )}
          </div>

          <div className="flex-1 overflow-y-auto space-y-1.5 pr-1">
            {sessions.length === 0 ? (
              <div className="text-center py-8 text-cinema-muted/50 text-xs">
                <Sparkles size={24} className="mx-auto mb-2 opacity-30" />
                <p>No previous conversations</p>
              </div>
            ) : (
              sessions.map((sess) => {
                const isActive = currentSessionId === sess.id
                return (
                  <div
                    key={sess.id}
                    onClick={() => loadSession(sess.id)}
                    className={`group w-full text-left p-3 sm:p-2.5 min-h-[52px] text-xs transition-all cursor-pointer flex items-center justify-between gap-2 ${
                      isActive
                        ? 'bg-cinema-glow/15 border border-cinema-glow/30 text-white'
                        : 'cinema-glass border border-white/5 text-cinema-muted hover:text-white hover:border-white/15'
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate text-white text-xs sm:text-sm">{sess.title || 'Untitled Session'}</p>
                      <div className="flex items-center gap-2 mt-1 text-xs text-cinema-muted">
                        <span>{sess.course_code || 'Course'}</span>
                        <span>•</span>
                        <span>{new Date(sess.last_message_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <button
                      onClick={(e) => handleDeleteSession(e, sess.id)}
                      className="opacity-100 sm:opacity-0 sm:group-hover:opacity-100 p-2 min-h-[40px] min-w-[40px] hover:bg-red-500/20 text-cinema-muted hover:text-red-400 transition-all flex-shrink-0 flex items-center justify-center"
                      title="Delete conversation"
                      aria-label="Delete conversation"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                )
              })
            )}
          </div>
        </aside>

        {/* Chat Feed Area */}
        <section className="flex-1 flex flex-col cinema-glass-strong border-0 sm:border sm:border-white/10 overflow-hidden min-w-0">
          {assessment ? (
            <AITutorAssessment
              assessment={assessment}
              submitting={assessmentSubmitting}
              lastResult={assessmentResult}
              selectedAnswer={assessmentAnswer}
              onSelectAnswer={setAssessmentAnswer}
              onSubmit={handleSubmitAssessmentAnswer}
              onContinue={handleContinueAssessment}
              onPracticeAgain={() => startQuickCheck({
                topic: assessment.topic,
                source: 'tutor'
              })}
              onContinueLearning={() => {
                setAssessment(null)
                setAssessmentAnswer('')
                setAssessmentResult(null)
              }}
              onClose={() => {
                setAssessment(null)
                setAssessmentAnswer('')
                setAssessmentResult(null)
              }}
            />
          ) : (
            <>
          {/* Context Selectors Bar */}
          <div className="border-b border-white/8 bg-white/2 flex-shrink-0">
            <button
              type="button"
              onClick={() => setContextOpen((open) => !open)}
              className={`${messages.length === 0 ? 'hidden' : 'flex'} sm:hidden w-full items-center justify-between gap-2 px-3 py-2.5 min-h-[44px] text-left`}
              aria-expanded={contextOpen}
            >
              <span className="flex items-center gap-1.5 min-w-0">
                <BookOpen size={13} className="text-cinema-gold flex-shrink-0" />
                <span className="text-xs text-white truncate">{contextSummary}</span>
              </span>
              {contextOpen ? <ChevronUp size={14} className="text-cinema-muted flex-shrink-0" /> : <ChevronDown size={14} className="text-cinema-muted flex-shrink-0" />}
            </button>

            <div className={`${contextOpen || messages.length === 0 ? 'flex' : 'hidden'} sm:flex p-2.5 sm:p-3 sm:px-4 flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center justify-between gap-2`}>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 flex-1 min-w-0">
                <div className="flex items-center gap-1.5 w-full sm:min-w-[180px] sm:w-auto sm:flex-1">
                  <BookOpen size={14} className="text-cinema-gold flex-shrink-0 hidden sm:block" />
                  <select
                    value={selectedCourseId || ''}
                    onChange={(e) => {
                      const cid = Number(e.target.value) || null
                      setSelectedCourseId(cid)
                      setSelectedLessonId(null)
                      loadLearningProfile(cid)
                    }}
                    className="bg-cinema-charcoal/80 border border-white/10 px-2.5 py-2.5 sm:py-1 text-sm sm:text-xs text-white focus:outline-none focus:border-cinema-glow/50 w-full min-w-0 truncate"
                  >
                    {courses.length === 0 ? (
                      <option value="">No active enrolled courses</option>
                    ) : (
                      courses.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.courseCode} — {c.title}
                        </option>
                      ))
                    )}
                  </select>
                </div>

                {availableLessons.length > 0 && (
                  <div className="flex items-center gap-1.5 w-full sm:min-w-[180px] sm:w-auto sm:flex-1">
                    <Layers size={14} className="text-cinema-glow flex-shrink-0 hidden sm:block" />
                    <select
                      value={selectedLessonId || ''}
                      onChange={(e) => setSelectedLessonId(Number(e.target.value) || null)}
                      className="bg-cinema-charcoal/80 border border-white/10 px-2.5 py-2.5 sm:py-1 text-sm sm:text-xs text-cinema-soft focus:outline-none focus:border-cinema-glow/50 w-full min-w-0 truncate"
                    >
                      <option value="">Whole Course Focus</option>
                      {availableLessons.map((l) => (
                        <option key={l.id} value={l.id} disabled={!l.isUnlocked}>
                          {l.isUnlocked ? `Lesson ${l.lessonNumber || ''}: ${l.title}` : `🔒 Lesson ${l.lessonNumber || ''}: ${l.title} (Locked)`}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {selectedLessonId && !liveJoin?.applied && (
                <span className="text-xs text-cinema-glow font-mono bg-cinema-glow/10 px-2.5 py-0.5 border border-cinema-glow/20 hidden sm:inline font-medium">
                  Lesson Context Active
                </span>
              )}
            </div>
            {liveJoin?.applied && (
              <div className="px-3 pb-2.5 sm:px-4 flex items-start gap-2">
                <Radio size={14} className="text-cinema-gold flex-shrink-0 mt-0.5" />
                <p className="font-sans text-xs sm:text-sm text-cinema-gold font-medium">
                  {liveLessonTitle
                    ? `Live class · ${liveLessonTitle}`
                    : 'A class is live. Your tutor is following the class when the current topic is available.'}
                </p>
              </div>
            )}
          </div>

          {/* Messages Stream */}
          <div className="flex-1 overflow-y-auto overscroll-contain p-3 sm:p-6 space-y-3 sm:space-y-4">
            {messages.length === 0 ? (
              <div className="h-full flex flex-col justify-end sm:items-center sm:justify-center text-left sm:text-center max-w-lg mx-auto py-2 sm:py-8">
                <div className="hidden sm:flex w-14 h-14 bg-cinema-glow/10 border border-cinema-glow/30 items-center justify-center text-cinema-glow mb-4 shadow-xl shadow-cinema-glow/15">
                  <Sparkles size={28} />
                </div>
                <h2 className="font-display text-lg sm:text-xl text-white font-medium mb-1 sm:mb-2">
                  Hi{studentFirstName ? ` ${studentFirstName}` : ''}.
                </h2>
                <p className="hidden sm:block font-sans text-xs text-cinema-muted mb-4 leading-relaxed">
                  I&apos;m here to help you understand {selectedCourse?.title || 'your course'}, practice what you&apos;re learning, and work through difficult concepts with you.
                  You can ask me to explain a concept, give you a practical example, quiz you, help you troubleshoot a project, or review a lesson.
                </p>
                <p className="font-sans text-xs text-cinema-soft mb-3 sm:mb-6">
                  What are you working on in {selectedCourse?.title || 'your course'}?
                </p>

                <div className="sm:hidden flex items-center gap-2 w-full overflow-x-auto no-scrollbar pb-1">
                  <button
                    type="button"
                    onClick={() => startQuickCheck({ source: selectedLessonId ? 'lesson' : 'tutor' })}
                    disabled={sending || assessmentSubmitting || usage.turnsUsed >= usage.dailyLimit}
                    className="inline-flex items-center gap-1.5 px-3 py-2.5 min-h-[44px] cinema-glass border border-cinema-gold/30 text-cinema-gold text-xs whitespace-nowrap shrink-0 disabled:opacity-40"
                  >
                    <ClipboardCheck size={13} />
                    Test me
                  </button>
                  {promptStarters.map((starter, i) => {
                    const Icon = starter.icon || Lightbulb
                    return (
                      <button
                        key={i}
                        type="button"
                        onClick={() => handleSendMessage(starter.text)}
                        className="inline-flex items-center gap-1.5 px-3 py-2.5 min-h-[44px] cinema-glass border border-white/10 text-cinema-soft text-xs whitespace-nowrap shrink-0"
                      >
                        <Icon size={13} />
                        {starter.label}
                      </button>
                    )
                  })}
                </div>

                <div className="hidden sm:grid grid-cols-2 gap-2 w-full">
                  <button
                    type="button"
                    onClick={() => startQuickCheck({ source: selectedLessonId ? 'lesson' : 'tutor' })}
                    disabled={sending || assessmentSubmitting || usage.turnsUsed >= usage.dailyLimit}
                    className="p-3 cinema-glass border border-cinema-gold/25 hover:border-cinema-gold/50 text-left transition-all col-span-2"
                  >
                    <div className="flex items-center gap-1.5 text-cinema-gold text-xs font-medium mb-1">
                      <ClipboardCheck size={13} />
                      <span>Test My Understanding</span>
                    </div>
                    <p className="text-xs text-cinema-muted">
                      Take a short 3-question check on {selectedLesson?.title || selectedCourse?.title || 'this topic'}.
                    </p>
                  </button>
                  {promptStarters.map((starter, i) => {
                    const Icon = starter.icon || Lightbulb
                    return (
                      <button
                        key={i}
                        onClick={() => handleSendMessage(starter.text)}
                        className="p-3 cinema-glass border border-white/8 hover:border-cinema-glow/40 text-left transition-all group"
                      >
                        <div className="flex items-center gap-1.5 text-cinema-glow text-xs font-medium mb-1">
                          <Icon size={13} />
                          <span>{starter.label}</span>
                        </div>
                        <p className="text-xs text-cinema-muted group-hover:text-cinema-soft line-clamp-2">
                          {starter.text}
                        </p>
                      </button>
                    )
                  })}
                </div>
              </div>
            ) : (
              messages.map((msg, index) => {
                const isUser = msg.role === 'user'
                return (
                  <div
                    key={msg.id || index}
                    className={`flex gap-2 sm:gap-3 ${isUser ? 'justify-end' : 'justify-start'}`}
                  >
                    {!isUser && (
                      <div className="hidden sm:flex w-8 h-8 bg-cinema-gold/15 border border-cinema-gold/40 items-center justify-center text-cinema-gold flex-shrink-0 mt-1">
                        <Sparkles size={16} />
                      </div>
                    )}

                    <div
                      className={`max-w-[88%] sm:max-w-[75%] px-3 py-2.5 sm:p-4 text-[0.9375rem] sm:text-base leading-relaxed shadow-lg break-words ${
                        isUser
                          ? 'bg-cinema-glow/25 border border-cinema-glow/50 text-white'
                          : msg.isError
                          ? 'bg-red-500/10 border border-red-500/30 text-red-300'
                          : 'bg-cinema-charcoal/70 border border-cinema-gold/20 text-cinema-soft'
                      }`}
                    >
                      {isUser || msg.isError ? (
                        <div className="whitespace-pre-wrap font-sans">
                          {msg.content}
                        </div>
                      ) : (
                        <TutorRichText content={msg.content} />
                      )}

                      <div className="flex items-center justify-between gap-3 mt-2 pt-1.5 text-xs text-cinema-muted/80">
                        <span className={isUser ? 'text-cinema-glow font-medium' : 'text-cinema-gold font-medium'}>
                          {isUser ? 'You' : 'SMA Tutor'}
                        </span>
                        <span>{msg.created_at || msg.createdAt ? new Date(msg.created_at || msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                      </div>
                    </div>

                    {isUser && (
                      <div className="hidden sm:flex w-8 h-8 bg-cinema-glow/20 border border-cinema-glow/40 items-center justify-center text-white flex-shrink-0 mt-1 font-semibold text-xs">
                        {studentFirstName ? studentFirstName[0].toUpperCase() : 'S'}
                      </div>
                    )}
                  </div>
                )
              })
            )}

            {sending && (
              <div className="flex gap-2 sm:gap-3 justify-start items-center text-cinema-muted text-xs">
                <div className="hidden sm:flex w-8 h-8 bg-cinema-gold/15 border border-cinema-gold/40 items-center justify-center text-cinema-gold flex-shrink-0 animate-pulse">
                  <Sparkles size={16} />
                </div>
                <div className="cinema-glass px-4 py-2.5 border border-white/10 flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-cinema-gold animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-1.5 h-1.5 bg-cinema-gold animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-1.5 h-1.5 bg-cinema-gold animate-bounce" style={{ animationDelay: '300ms' }} />
                  <span className="text-cinema-muted text-xs ml-1 font-sans">Thinking with you...</span>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input Box Bar */}
          <div className="p-2.5 sm:p-4 border-t border-white/10 bg-cinema-black/95 sm:bg-white/2 flex-shrink-0 pb-[max(0.65rem,env(safe-area-inset-bottom))]">
            {messages.length > 0 && !keyboardOpen && (
              <div className="flex items-center gap-1.5 mb-2 overflow-x-auto no-scrollbar">
                {quickActions.map((action) => {
                  const Icon = action.icon || Lightbulb
                  return (
                    <button
                      key={action.label}
                      type="button"
                      onClick={() => handleSendMessage(action.text)}
                      disabled={sending || usage.turnsUsed >= usage.dailyLimit}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs cinema-glass border border-white/10 text-cinema-muted hover:text-white hover:border-cinema-glow/40 disabled:opacity-40 whitespace-nowrap shrink-0 font-medium"
                    >
                      <Icon size={12} />
                      {action.label}
                    </button>
                  )
                })}
              </div>
            )}

            {/* Phase 6: Agent mode toggle */}
            <div className="flex items-center gap-2 mb-2">
              <button
                type="button"
                onClick={() => setAgentMode(!agentMode)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium border transition-colors ${
                  agentMode
                    ? 'bg-cinema-glow/20 border-cinema-glow/50 text-cinema-glow'
                    : 'cinema-glass border-white/10 text-cinema-muted hover:text-white'
                }`}
                title="Learning Assistant mode uses AI tools to check your real progress, lessons, and assignments"
              >
                <Sparkles size={12} />
                {agentMode ? 'Learning Assistant' : 'Tutor Chat'}
              </button>
              {agentMode && (
                <span className="text-xs text-cinema-muted/60">Uses your real LMS data · curriculum-grounded</span>
              )}
            </div>

            <div className="relative flex items-end gap-2 cinema-glass border border-white/10 p-1.5 sm:p-2 focus-within:border-cinema-glow/50 transition-colors">
              <textarea
                ref={textareaRef}
                value={inputText}
                onChange={(e) => {
                  setInputText(e.target.value)
                  e.target.style.height = 'auto'
                  e.target.style.height = `${Math.min(140, e.target.scrollHeight)}px`
                }}
                onKeyDown={handleKeyDown}
                placeholder={
                  usage.turnsUsed >= usage.dailyLimit
                    ? 'Daily question quota reached (100/day).'
                    : `Ask about ${selectedCourse?.title || 'your course'}…`
                }
                disabled={sending || usage.turnsUsed >= usage.dailyLimit}
                rows={1}
                maxLength={1000}
                enterKeyHint="send"
                className="flex-1 bg-transparent border-0 text-white placeholder:text-cinema-muted/50 text-base sm:text-sm focus:outline-none resize-none px-2 py-2 max-h-36 disabled:opacity-50 min-w-0"
              />

              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-xs text-cinema-muted/60 font-mono hidden sm:inline">
                  {inputText.length}/1000
                </span>
                <button
                  type="button"
                  onClick={() => handleSendMessage()}
                  disabled={!inputText.trim() || sending || usage.turnsUsed >= usage.dailyLimit}
                  className="w-11 h-11 sm:w-9 sm:h-9 cinema-btn-primary !min-h-0 !px-0 flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-md shadow-cinema-glow/10"
                  title="Send message (Enter)"
                  aria-label="Send message"
                >
                  <Send size={15} />
                </button>
              </div>
            </div>

            {!keyboardOpen && (
              <div className="hidden sm:flex items-center justify-between text-xs text-cinema-muted/60 mt-2 px-1">
                <span>I will guide you with questions, examples, and feedback — not by doing the work for you.</span>
                <span className="shrink-0 ml-2">Shift + Enter for new line</span>
              </div>
            )}
          </div>
            </>
          )}
        </section>
      </div>
    </div>
  )
}
